import type {
    Nullable,
    Query,
    QueryObserver,
    Queryable,
} from './interfaces/Frameworks';

import _ from 'lodash';

class QueryImpl<T> implements Query<T> {
    private observer: Nullable<QueryObserver<T>>;
    private queryable: Queryable<T>;

    constructor(queryable: Queryable<T>) {
        this.queryable = queryable;
        this.observer = null;
    }

    observe(observer: QueryObserver<T>): void {
        if (this.observer) {
            throw new Error('QueryImpl - observe(): An observer is already registered for this query.');
        }
        this.queryable.addQueryObserver(observer);
        this.observer = observer;
    }

    close(): void {
        if (this.observer) {
            this.queryable.removeQueryObserver(this.observer);
            this.observer = null;
        }
    }

    getObserver(): Nullable<QueryObserver<T>> {
        return this.observer;
    }
}

function newQuery<T>(queryable: Queryable<T>): Query<T> {
    return new QueryImpl(queryable);
}

class QueryableImpl<T> implements Queryable<T> {
    private state: T;
    private queryObservers: Array<QueryObserver<T>>;
    private identifier: string;
    private filteredStateKeys: Nullable<Array<string>>;

    constructor(initialState: T, identifier: string, filteredStateKeys: Nullable<Array<string>>) {
        this.state = initialState;
        this.identifier = identifier;
        this.queryObservers = [];
        this.filteredStateKeys = filteredStateKeys;
    }

    query(): Query<T> {
        return newQuery(this);
    }

    getState(): T {
        return this.state;
    }

    setState(update: Partial<T>): void {
        let newState = {} as T;
        _.assign(newState, this.state, update);
        if (!_.isEqual(this.state, newState)) {
            this.state = newState;
            this.notifyQueryObservers();
        }
    }

    addQueryObserver(observer: QueryObserver<T>): void {
        if (!this.hasQueryObserver(observer)) {
            this.queryObservers.push(observer);
            observer.changed(this.getState());
        }
    }

    removeQueryObserver(observer: QueryObserver<T>): void {
        if (this.hasQueryObserver(observer)) {
            _.remove(this.queryObservers, (o: QueryObserver<T>) => o === observer);
        }
    }

    notifyQueryObservers(): void {
        this.queryObservers.forEach((o: QueryObserver<T>) => {
            try {
                o.changed(this.state);
            }
            catch (e) {
                console.error(`QUERYABLE: notify observer: - error: ${e}`);
            }
        });
    }

    hasQueryObserver(observer: QueryObserver<T>): boolean {
        return _.includes(this.queryObservers, observer);
    }

    getQueryObservers(): Array<QueryObserver<T>> {
        return this.queryObservers;
    }
}

function newQueryable<T>(state: T, identifier: string, filteredStateKeys?: Array<string>): Queryable<T> {
    return new QueryableImpl(state, identifier, filteredStateKeys);
}

export { newQueryable };
